import { createSelector } from '@reduxjs/toolkit'

import { errorView, queryView, type QueryResultLike } from '@krp/client-runtime'

import { ONEC_PROFILES } from './settings'
import type { OneCCheckResultVm, OneCConnectionCheck, OneCDiagnosticsVm, OneCProfile } from './types'

/** Результат мутации проверки — поля хука `useMutation`; аргументы в vm не входят. */
export interface OneCCheckResultLike {
  data?: OneCConnectionCheck
  error?: unknown
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
}

export interface OneCDiagnosticsVmInput {
  profile: QueryResultLike<OneCProfile>
  check: OneCCheckResultLike
}

function checkVm(check: OneCConnectionCheck): OneCCheckResultVm {
  const outcome = check.result === 'reachable' || check.result === 'unauthorized' ? check.result : 'unavailable'
  const title =
    outcome === 'reachable'
      ? '1С отвечает'
      : outcome === 'unauthorized'
        ? '1С отклонила логин или пароль'
        : '1С недоступна'
  return {
    outcome,
    title,
    message: check.message,
    remoteStatus: check.remoteStatus,
    remembered: check.fromRememberedRefusal,
  }
}

/** Фабрика vm панели диагностики одной базы 1С: по экземпляру на смонтированный binding. */
export const makeSelectOneCDiagnosticsVm = () =>
  createSelector(
    [
      (input: OneCDiagnosticsVmInput) => input.profile.isUninitialized,
      (input: OneCDiagnosticsVmInput) => input.profile.isLoading,
      (input: OneCDiagnosticsVmInput) => input.profile.isError,
      (input: OneCDiagnosticsVmInput) => input.profile.isSuccess,
      (input: OneCDiagnosticsVmInput) => input.profile.error,
      (input: OneCDiagnosticsVmInput) => input.profile.data,
      (input: OneCDiagnosticsVmInput) => input.check.isLoading,
      (input: OneCDiagnosticsVmInput) => input.check.isError,
      (input: OneCDiagnosticsVmInput) => input.check.error,
      (input: OneCDiagnosticsVmInput) => input.check.data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data, checking, checkFailed, checkError, checked) => {
      const query = queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        () => false,
      )
      const value = query.status === 'ready' ? (data ?? null) : null
      const failure = checkFailed && checkError !== undefined ? errorView(checkError) : null
      const vm: OneCDiagnosticsVm = {
        profile: {
          query,
          value,
          profileTitle:
            value === null
              ? ''
              : (ONEC_PROFILES.find((item) => item.value === value.configurationProfile)?.label ??
                value.configurationProfile),
        },
        check: {
          busy: checking,
          result: !checking && !checkFailed && checked !== undefined ? checkVm(checked) : null,
          error:
            failure === null
              ? null
              : { message: failure.message, code: failure.code, correlationId: failure.correlationId },
        },
      }
      return vm
    },
  )
